<template>
  <div class="tags-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader />
      <main class="page-content">
        <div class="tags-container">

          <!-- Page Header -->
          <div class="page-header">
            <p class="page-desc">Create and manage tags to label and segment your customers</p>
            <button @click="startCreating" class="btn-new" :disabled="isCreating">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              New Tag
            </button>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading tags...</p>
          </div>

          <!-- Tags Grid -->
          <div v-else class="tags-grid">

            <!-- Inline Create Card -->
            <transition name="card-slide">
              <div v-if="isCreating" class="tag-card create-card">
                <div class="color-strip" :style="{ background: newTag.color }"></div>
                <div class="card-body">
                  <p class="section-label">Pick a color</p>
                  <div class="color-picker">
                    <button
                      v-for="c in COLORS"
                      :key="c"
                      class="color-swatch"
                      :class="{ active: newTag.color === c }"
                      :style="{ background: c }"
                      @click.prevent="newTag.color = c"
                      :title="c"
                    ></button>
                  </div>
                  <div class="name-row">
                    <span class="tag-dot-lg" :style="{ background: newTag.color }"></span>
                    <input
                      ref="createInput"
                      v-model="newTag.name"
                      class="tag-name-input"
                      placeholder="Tag name..."
                      maxlength="30"
                      @keydown.enter.prevent="saveNew"
                      @keydown.escape="cancelCreate"
                    />
                  </div>
                  <p class="input-hint">Press Enter to save · Esc to cancel</p>
                  <div class="card-actions">
                    <button
                      @click="saveNew"
                      class="btn-save"
                      :disabled="!newTag.name.trim() || saving"
                    >
                      <div v-if="saving" class="btn-spinner"></div>
                      <span v-else>Save Tag</span>
                    </button>
                    <button @click="cancelCreate" class="btn-cancel">Cancel</button>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Tag Cards -->
            <div
              v-for="tag in tags"
              :key="tag.id"
              class="tag-card"
              :class="{
                'is-editing': editingId === tag.id,
                'is-confirming': confirmDeleteId === tag.id
              }"
            >
              <!-- Color header -->
              <div
                class="color-header"
                :style="{ background: editingId === tag.id ? editTag.color : tag.color }"
              ></div>

              <div class="card-body">

                <!-- Edit state -->
                <template v-if="editingId === tag.id">
                  <p class="section-label">Pick a color</p>
                  <div class="color-picker">
                    <button
                      v-for="c in COLORS"
                      :key="c"
                      class="color-swatch"
                      :class="{ active: editTag.color === c }"
                      :style="{ background: c }"
                      @click.prevent="editTag.color = c"
                    ></button>
                  </div>
                  <div class="name-row">
                    <span class="tag-dot-lg" :style="{ background: editTag.color }"></span>
                    <input
                      ref="editInput"
                      v-model="editTag.name"
                      class="tag-name-input"
                      maxlength="30"
                      @keydown.enter.prevent="saveEdit(tag)"
                      @keydown.escape="cancelEdit"
                    />
                  </div>
                  <p class="input-hint">Press Enter to save · Esc to cancel</p>
                  <div class="card-actions">
                    <button
                      @click="saveEdit(tag)"
                      class="btn-save"
                      :disabled="!editTag.name.trim() || saving"
                    >
                      <div v-if="saving" class="btn-spinner"></div>
                      <span v-else>Update</span>
                    </button>
                    <button @click="cancelEdit" class="btn-cancel">Cancel</button>
                  </div>
                </template>

                <!-- Delete confirm state -->
                <template v-else-if="confirmDeleteId === tag.id">
                  <div class="delete-confirm">
                    <div class="del-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                    </div>
                    <p class="del-title">Delete <strong>"{{ tag.name }}"</strong>?</p>
                    <p v-if="tag.customer_count > 0" class="del-warning">
                      Removes this tag from {{ tag.customer_count }}
                      {{ tag.customer_count === 1 ? 'customer' : 'customers' }}.
                    </p>
                    <p v-else class="del-safe">No customers currently use this tag.</p>
                    <div class="card-actions">
                      <button
                        @click="deleteTag(tag)"
                        class="btn-delete-confirm"
                        :disabled="deleting === tag.id"
                      >
                        <div v-if="deleting === tag.id" class="btn-spinner white"></div>
                        <span v-else>Yes, Delete</span>
                      </button>
                      <button @click="confirmDeleteId = null" class="btn-cancel">Keep</button>
                    </div>
                  </div>
                </template>

                <!-- Normal state -->
                <template v-else>
                  <p class="tag-name">{{ tag.name }}</p>
                  <span class="customer-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                    </svg>
                    {{ tag.customer_count || 0 }} {{ (tag.customer_count || 0) === 1 ? 'customer' : 'customers' }}
                  </span>
                  <div class="hover-actions">
                    <button @click="startEdit(tag)" class="action-btn edit-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                      Edit
                    </button>
                    <button @click="confirmDeleteId = tag.id" class="action-btn delete-btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                </template>

              </div>
            </div>

            <!-- Empty state -->
            <div v-if="!isCreating && tags.length === 0" class="empty-state">
              <div class="empty-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>
                </svg>
              </div>
              <h3>No tags yet</h3>
              <p>Create your first tag to start organizing and segmenting your customers</p>
              <button @click="startCreating" class="btn-new-lg">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Create First Tag
              </button>
            </div>

          </div>
        </div>
      </main>
    </div>

    <!-- Toast -->
    <transition name="toast">
      <div v-if="toast.show" class="toast" :class="toast.type">
        <svg v-if="toast.type === 'success'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        {{ toast.message }}
      </div>
    </transition>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { AuthService } from '@/services/auth'

const API_BASE = 'https://api.quali-d.com'

const COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#22C55E', // green
  '#10B981', // emerald
  '#14B8A6', // teal
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#6B7280' // gray
]

export default {
  name: 'CustomerTagsView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      COLORS,
      loading: true,
      tags: [],

      // Create
      isCreating: false,
      newTag: { name: '', color: COLORS[7] }, // default blue
      saving: false,

      // Edit
      editingId: null,
      editTag: { name: '', color: '' },

      // Delete
      confirmDeleteId: null,
      deleting: null,

      // Toast
      toast: { show: false, type: 'success', message: '' }
    }
  },

  async mounted () {
    await this.loadTags()
  },

  methods: {
    // ─── API helper ───────────────────────────────────────────────────────────
    async request (endpoint, options = {}) {
      const token = AuthService.getAuthToken()
      const res = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(options.headers || {})
        }
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
      return data
    },

    // ─── Load ─────────────────────────────────────────────────────────────────
    async loadTags () {
      this.loading = true
      try {
        const data = await this.request('/customer-tags')
        this.tags = data.tags || []
      } catch (e) {
        this.showToast('error', 'Failed to load tags')
      } finally {
        this.loading = false
      }
    },

    // ─── Create ───────────────────────────────────────────────────────────────
    startCreating () {
      this.cancelEdit()
      this.confirmDeleteId = null
      this.isCreating = true
      this.newTag = { name: '', color: COLORS[7] }
      this.$nextTick(() => this.$refs.createInput?.focus())
    },

    cancelCreate () {
      this.isCreating = false
      this.newTag = { name: '', color: COLORS[7] }
    },

    async saveNew () {
      const name = this.newTag.name.trim()
      if (!name) return
      this.saving = true
      try {
        const data = await this.request('/customer-tags', {
          method: 'POST',
          body: JSON.stringify({ name, color: this.newTag.color })
        })
        this.tags.unshift({ id: data.id, name, color: this.newTag.color, customer_count: 0 })
        this.isCreating = false
        this.newTag = { name: '', color: COLORS[7] }
        this.showToast('success', `Tag "${name}" created`)
      } catch (e) {
        this.showToast('error', e.message || 'Failed to create tag')
      } finally {
        this.saving = false
      }
    },

    // ─── Edit ─────────────────────────────────────────────────────────────────
    startEdit (tag) {
      this.cancelCreate()
      this.confirmDeleteId = null
      this.editingId = tag.id
      this.editTag = { name: tag.name, color: tag.color }
      this.$nextTick(() => this.$refs.editInput?.[0]?.focus())
    },

    cancelEdit () {
      this.editingId = null
      this.editTag = { name: '', color: '' }
    },

    async saveEdit (tag) {
      const name = this.editTag.name.trim()
      if (!name) return
      this.saving = true
      try {
        await this.request(`/customer-tags/${tag.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name, color: this.editTag.color })
        })
        const idx = this.tags.findIndex(t => t.id === tag.id)
        if (idx !== -1) {
          this.tags[idx] = { ...this.tags[idx], name, color: this.editTag.color }
        }
        this.editingId = null
        this.showToast('success', 'Tag updated')
      } catch (e) {
        this.showToast('error', e.message || 'Failed to update tag')
      } finally {
        this.saving = false
      }
    },

    // ─── Delete ───────────────────────────────────────────────────────────────
    async deleteTag (tag) {
      this.deleting = tag.id
      try {
        await this.request(`/customer-tags/${tag.id}`, { method: 'DELETE' })
        this.tags = this.tags.filter(t => t.id !== tag.id)
        this.confirmDeleteId = null
        this.showToast('success', `Tag "${tag.name}" deleted`)
      } catch (e) {
        this.showToast('error', e.message || 'Failed to delete tag')
      } finally {
        this.deleting = null
      }
    },

    // ─── Toast ────────────────────────────────────────────────────────────────
    showToast (type, message) {
      this.toast = { show: true, type, message }
      setTimeout(() => { this.toast.show = false }, 3500)
    }
  }
}
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────────────────────────────── */
.tags-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.page-content {
  padding: 32px;
  flex: 1;
}

.tags-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* ── Page Header ─────────────────────────────────────────────────────────── */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 20px;
}

.page-desc {
  font-size: 15px;
  color: var(--text-secondary);
  margin: 0;
}

/* ── Buttons ─────────────────────────────────────────────────────────────── */
.btn-new {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #6366F1;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-new:hover:not(:disabled) {
  background: #4F46E5;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
}

.btn-new:disabled {
  opacity: 0.5;
  cursor: default;
}

.btn-new-lg {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #6366F1;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  margin-top: 8px;
}

.btn-new-lg:hover {
  background: #4F46E5;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
}

.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: #6366F1;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-save:hover:not(:disabled) {
  background: #4F46E5;
}

.btn-save:disabled {
  opacity: 0.45;
  cursor: default;
}

.btn-cancel {
  padding: 8px 16px;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-cancel:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.btn-delete-confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: #EF4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-delete-confirm:hover:not(:disabled) {
  background: #DC2626;
}

.btn-delete-confirm:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ── Loading ─────────────────────────────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
  color: var(--text-secondary);
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border-primary);
  border-top-color: #6366F1;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* ── Tags Grid ───────────────────────────────────────────────────────────── */
.tags-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  align-items: start;
}

/* ── Tag Card ────────────────────────────────────────────────────────────── */
.tag-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 14px;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s;
  position: relative;
}

.tag-card:not(.create-card):not(.is-editing):not(.is-confirming):hover {
  border-color: #6366F1;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.12);
  transform: translateY(-2px);
}

.tag-card.is-editing {
  border-color: #6366F1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.tag-card.is-confirming {
  border-color: #EF4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

/* ── Color Header ────────────────────────────────────────────────────────── */
.color-header {
  height: 40px;
  width: 100%;
  transition: background 0.2s;
}

.card-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
}

/* ── Normal State ────────────────────────────────────────────────────────── */
.tag-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.customer-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
}

/* Card actions — always visible */
.hover-actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: 2px;
}

.action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 0;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.action-btn.edit-btn:hover {
  background: #EEF2FF;
  border-color: #6366F1;
  color: #6366F1;
}

.action-btn.delete-btn:hover {
  background: #FEF2F2;
  border-color: #EF4444;
  color: #EF4444;
}

/* ── Color Picker ────────────────────────────────────────────────────────── */
.section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  margin: 0 0 8px;
}

.color-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-bottom: 14px;
}

.color-swatch {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.12s, border-color 0.12s;
  padding: 0;
  outline: none;
}

.color-swatch:hover {
  transform: scale(1.2);
}

.color-swatch.active {
  border-color: var(--text-primary);
  transform: scale(1.15);
  box-shadow: 0 0 0 2px var(--bg-primary), 0 0 0 4px currentColor;
}

/* ── Name Input Row ──────────────────────────────────────────────────────── */
.name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.tag-dot-lg {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  transition: background 0.15s;
}

.tag-name-input {
  flex: 1;
  background: var(--bg-secondary);
  border: 1.5px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.15s;
  width: 100%;
  min-width: 0;
}

.tag-name-input:focus {
  border-color: #6366F1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.input-hint {
  font-size: 11px;
  color: var(--text-secondary);
  margin: 0 0 14px;
  opacity: 0.7;
}

/* ── Card Actions Row ────────────────────────────────────────────────────── */
.card-actions {
  display: flex;
  gap: 8px;
}

/* ── Create Card specific ────────────────────────────────────────────────── */
.create-card {
  border-color: #6366F1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

/* ── Delete Confirm ──────────────────────────────────────────────────────── */
.delete-confirm {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 4px 0;
}

.del-icon {
  color: #EF4444;
  opacity: 0.8;
  margin-bottom: 4px;
}

.del-title {
  font-size: 14px;
  color: var(--text-primary);
  margin: 0;
}

.del-warning {
  font-size: 12px;
  color: #F97316;
  margin: 0;
}

.del-safe {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0;
}

.delete-confirm .card-actions {
  justify-content: center;
  margin-top: 8px;
}

/* ── Empty State ─────────────────────────────────────────────────────────── */
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  color: var(--text-secondary);
  opacity: 0.25;
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 10px;
}

.empty-state p {
  font-size: 15px;
  color: var(--text-secondary);
  margin: 0;
  max-width: 420px;
}

/* ── Toast ───────────────────────────────────────────────────────────────── */
.toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  z-index: 9999;
  pointer-events: none;
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
}

.toast.success { background: #10B981; }
.toast.error   { background: #EF4444; }

.toast-enter-active, .toast-leave-active {
  transition: all 0.25s ease;
}

.toast-enter-from, .toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}

/* ── Btn spinner ─────────────────────────────────────────────────────────── */
.btn-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.btn-spinner.white {
  border-top-color: white;
}

/* ── Card slide animation ────────────────────────────────────────────────── */
.card-slide-enter-active {
  transition: all 0.22s ease;
}

.card-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}

/* ── Keyframes ───────────────────────────────────────────────────────────── */
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Responsive ──────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 0 !important;
  }
}

@media (max-width: 768px) {
  .tags-layout {
    overflow-x: hidden;
  }

  .main-content {
    margin-left: 0 !important;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .page-content {
    padding: 20px 16px;
  }

  .tags-grid {
    grid-template-columns: 1fr;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .btn-new {
    width: 100%;
    justify-content: center;
  }
}
</style>
